import React from "react";
import coachPortrait from "../assets/images/about-coach-portrait-bw.jpg";
import { useLanguage } from "../i18n/LanguageContext";
import "./AboutCoach.css";

// Newest first, the way the credentials are listed publicly. Titles and issuers
// are proper nouns, so they stay untranslated while the labels around them do not.
// Deliberately undated: a credential is either held or it is not.
const certifications = [
  {
    title: "CPR & AED",
    issuer: "ProMed Certifications",
    icon: "fa-heart-pulse",
  },
  {
    title: "Strength and Conditioning Coach Certified (SCCC)",
    issuer: "DARU STRONG",
    icon: "fa-dumbbell",
  },
  {
    title: "Barbell Speciality Certification",
    issuer: "RumbleFC",
    icon: "fa-weight-hanging",
  },
  {
    title: "Kettlebell Specialty Certification Level 2",
    issuer: "RumbleFC",
    icon: "fa-fire-flame-curved",
  },
  {
    title: "Kettlebell Specialty Certification Level 1",
    issuer: "RumbleFC",
    icon: "fa-bolt",
  },
  {
    title: "Facility And Sports Management",
    issuer: "Performance First",
    icon: "fa-clipboard-list",
  },
  {
    title: "Certified Personal Fitness Trainer – Health Fitness Instructor (CPFT)",
    issuer: "Stepahead Holding",
    icon: "fa-user-check",
  },
];

const domains = [
  "Athletic Training",
  "Olympic Weightlifting",
  "Kettlebell Coaching",
  "Functional Hypertrophy",
  "Rehabilitation Systems",
];

const issuerCount = new Set(certifications.map(({ issuer }) => issuer)).size;

const AboutCoach = () => {
  const { t } = useLanguage();

  return (
    <section
      id="about-coach"
      className="hpa-about-coach hpa-scroll-reveal"
      aria-labelledby="hpa-about-coach-title"
    >
      <div className="container">
        <div className="hpa-about-coach__shell">
          <figure className="hpa-about-coach__figure">
            <div className="hpa-about-coach__portrait">
              <img
                src={coachPortrait}
                alt={t("Elie Abboud, founder and head coach of Hybrid Performance Academy")}
                loading="lazy"
                decoding="async"
                width="1254"
                height="1254"
              />
              <span className="hpa-about-coach__glow" aria-hidden="true" />
            </div>

            {/* The count is derived so the seal can never drift from the list. */}
            <div className="hpa-about-coach__seal" aria-hidden="true">
              <strong>{certifications.length}</strong>
              <span>{t("Certifications")}</span>
            </div>

            <figcaption className="hpa-about-coach__nameplate">
              <strong>Elie Abboud</strong>
              <span>{t("Founder & Head Coach")}</span>
            </figcaption>
          </figure>

          <div className="hpa-about-coach__body">
            <span className="hpa-about-coach__kicker">{t("About the founder")}</span>

            <h2 id="hpa-about-coach-title" className="hpa-about-coach__title">
              {t("Built by a coach, not a content library.")}
            </h2>

            <p className="hpa-about-coach__lead">
              {t("Certified across multiple performance domains, I translate complex methodologies into practical, results-driven strategies for individuals and organizations alike.")}
            </p>

            <ul className="hpa-about-coach__domains">
              {domains.map((domain) => (
                <li key={domain}>{t(domain)}</li>
              ))}
            </ul>

            {/* The seal already carries the certification count, so these two
                say something it does not. Both derived, neither to keep in sync. */}
            <div className="hpa-about-coach__stats">
              <div>
                <strong>{domains.length}</strong>
                <span>{t("Performance domains")}</span>
              </div>
              <div>
                <strong>{issuerCount}</strong>
                <span>{t("Certifying bodies")}</span>
              </div>
            </div>

            <div className="hpa-about-coach__creds">
              <h3 className="hpa-about-coach__creds-title">
                <i className="fa-solid fa-award" aria-hidden="true" />
                {t("Licenses & certifications")}
              </h3>

              <ol className="hpa-credential-track">
                {certifications.map((certification) => (
                  <li className="hpa-credential" key={certification.title}>
                    <span className="hpa-credential__node" aria-hidden="true">
                      <i className={`fa-solid ${certification.icon}`} />
                    </span>

                    <div className="hpa-credential__body">
                      <p className="hpa-credential__title">{certification.title}</p>
                      <p className="hpa-credential__issuer">{certification.issuer}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutCoach;
